export interface WoodPalette {
  readonly name: string;
  readonly boardWoodBase: string;
  readonly boardWoodDark: string;
  readonly boardWoodLight: string;
  readonly borderWoodBase: string;
  readonly borderWoodDark: string;
  readonly borderWoodLight: string;
}

export interface PatternDimensions {
  readonly width: number;
  readonly height: number;
}

let cachedKey: string | null = null;
let cachedBoardPattern: CanvasPattern | null = null;
let cachedBorderPattern: CanvasPattern | null = null;
let cachedFeltPattern: CanvasPattern | null = null;

export function invalidateTextureCache(): void {
  cachedBoardPattern = null;
  cachedBorderPattern = null;
  cachedFeltPattern = null;
  cachedKey = null;
}

function cacheKey(palette: WoodPalette, dims: PatternDimensions): string {
  return `${palette.name}|${dims.width}x${dims.height}`;
}

function invalidateIfChanged(palette: WoodPalette, dims: PatternDimensions): void {
  const key = cacheKey(palette, dims);
  if (cachedKey !== key) {
    cachedBoardPattern = null;
    cachedBorderPattern = null;
    cachedKey = key;
  }
}

export function getWoodPattern(
  ctx: CanvasRenderingContext2D,
  palette: WoodPalette,
  dims: PatternDimensions,
): CanvasPattern {
  invalidateIfChanged(palette, dims);
  if (cachedBoardPattern) return cachedBoardPattern;
  cachedBoardPattern = createWoodPattern(
    ctx,
    palette.boardWoodBase,
    palette.boardWoodDark,
    palette.boardWoodLight,
    dims,
  );
  return cachedBoardPattern;
}

export function getBorderWoodPattern(
  ctx: CanvasRenderingContext2D,
  palette: WoodPalette,
  dims: PatternDimensions,
): CanvasPattern {
  invalidateIfChanged(palette, dims);
  if (cachedBorderPattern) return cachedBorderPattern;
  cachedBorderPattern = createWoodPattern(
    ctx,
    palette.borderWoodBase,
    palette.borderWoodDark,
    palette.borderWoodLight,
    dims,
  );
  return cachedBorderPattern;
}

// Tileable speckle noise — palette-independent. The alpha at the call site
// controls how strong it reads against the underlying square color.
export function getFeltNoisePattern(ctx: CanvasRenderingContext2D): CanvasPattern {
  if (cachedFeltPattern) return cachedFeltPattern;
  const size = 64;
  const off = document.createElement("canvas");
  off.width = size;
  off.height = size;
  const c = off.getContext("2d");
  if (!c) throw new Error("Could not create offscreen context");

  for (let i = 0; i < 1400; i++) {
    const r1 = pseudoRandom(i * 11 + 1);
    const r2 = pseudoRandom(i * 13 + 7);
    const r3 = pseudoRandom(i * 17 + 3);
    const r4 = pseudoRandom(i * 19 + 5);
    const x = r1 * size;
    const y = r2 * size;
    const isLight = r3 > 0.5;
    c.fillStyle = isLight
      ? `rgba(255, 255, 255, ${0.18 + r4 * 0.18})`
      : `rgba(0, 0, 0, ${0.18 + r4 * 0.18})`;
    c.fillRect(x, y, 1, 1);
  }

  const pattern = ctx.createPattern(off, "repeat");
  if (!pattern) throw new Error("Could not create felt pattern");
  cachedFeltPattern = pattern;
  return pattern;
}

function createWoodPattern(
  ctx: CanvasRenderingContext2D,
  baseColor: string,
  darkColor: string,
  lightColor: string,
  dims: PatternDimensions,
): CanvasPattern {
  const { width: w, height: h } = dims;
  const offscreen = document.createElement("canvas");
  offscreen.width = w;
  offscreen.height = h;
  const offCtx = offscreen.getContext("2d");
  if (!offCtx) throw new Error("Could not create offscreen context");

  const seed = hashColor(baseColor);

  offCtx.fillStyle = baseColor;
  offCtx.fillRect(0, 0, w, h);

  offCtx.globalAlpha = 0.08;
  for (let i = 0; i < 12; i++) {
    const bandY = pseudoRandom(seed + i * 97) * h;
    const bandH = 20 + pseudoRandom(seed + i * 101) * 60;
    const isDark = pseudoRandom(seed + i * 53) > 0.4;
    offCtx.fillStyle = isDark ? darkColor : lightColor;
    offCtx.fillRect(0, bandY - bandH / 2, w, bandH);
  }

  drawGrainLayer(offCtx, seed, w, h, darkColor, lightColor, {
    count: 140,
    alphaMin: 0.06,
    alphaMax: 0.2,
    thicknessMin: 0.3,
    thicknessMax: 3.5,
    amplitudeMin: 0.5,
    amplitudeMax: 6,
    frequencyMin: 0.003,
    frequencyMax: 0.02,
  });

  drawGrainLayer(offCtx, seed + 5000, w, h, darkColor, lightColor, {
    count: 60,
    alphaMin: 0.03,
    alphaMax: 0.08,
    thicknessMin: 0.2,
    thicknessMax: 1,
    amplitudeMin: 0.2,
    amplitudeMax: 2,
    frequencyMin: 0.015,
    frequencyMax: 0.04,
  });

  offCtx.globalAlpha = 0.07;
  for (let i = 0; i < 8; i++) {
    const kx = pseudoRandom(seed + i * 37) * w;
    const ky = pseudoRandom(seed + i * 41) * h;
    const kr = 8 + pseudoRandom(seed + i * 43) * 30;
    const angle = pseudoRandom(seed + i * 47) * Math.PI;
    const stretch = 1.2 + pseudoRandom(seed + i * 51) * 1.5;
    const grad = offCtx.createRadialGradient(kx, ky, 0, kx, ky, kr);
    grad.addColorStop(0, darkColor);
    grad.addColorStop(0.6, darkColor);
    grad.addColorStop(1, "transparent");
    offCtx.fillStyle = grad;
    offCtx.save();
    offCtx.translate(kx, ky);
    offCtx.rotate(angle);
    offCtx.scale(stretch, 1);
    offCtx.translate(-kx, -ky);
    offCtx.beginPath();
    offCtx.arc(kx, ky, kr, 0, Math.PI * 2);
    offCtx.fill();
    offCtx.restore();
  }

  offCtx.globalAlpha = 0.03;
  for (let i = 0; i < 300; i++) {
    const nx = pseudoRandom(seed + i * 71) * w;
    const ny = pseudoRandom(seed + i * 73) * h;
    const nr = 1 + pseudoRandom(seed + i * 79) * 3;
    offCtx.fillStyle = pseudoRandom(seed + i * 83) > 0.5 ? darkColor : lightColor;
    offCtx.beginPath();
    offCtx.arc(nx, ny, nr, 0, Math.PI * 2);
    offCtx.fill();
  }

  offCtx.globalAlpha = 1.0;

  const pattern = ctx.createPattern(offscreen, "repeat");
  if (!pattern) throw new Error("Could not create pattern");
  return pattern;
}

interface GrainOptions {
  readonly count: number;
  readonly alphaMin: number;
  readonly alphaMax: number;
  readonly thicknessMin: number;
  readonly thicknessMax: number;
  readonly amplitudeMin: number;
  readonly amplitudeMax: number;
  readonly frequencyMin: number;
  readonly frequencyMax: number;
}

function drawGrainLayer(
  ctx: CanvasRenderingContext2D,
  seed: number,
  w: number,
  h: number,
  darkColor: string,
  lightColor: string,
  opts: GrainOptions,
): void {
  for (let i = 0; i < opts.count; i++) {
    const r1 = pseudoRandom(seed + i * 7);
    const r2 = pseudoRandom(seed + i * 13);
    const r3 = pseudoRandom(seed + i * 19);
    const r4 = pseudoRandom(seed + i * 23);
    const r5 = pseudoRandom(seed + i * 29);
    const r6 = pseudoRandom(seed + i * 31);

    const y = r1 * h;
    const thickness = opts.thicknessMin + r2 * (opts.thicknessMax - opts.thicknessMin);
    const alpha = opts.alphaMin + r6 * (opts.alphaMax - opts.alphaMin);
    const isDark = pseudoRandom(seed + i * 3) > 0.45;

    ctx.globalAlpha = alpha;
    ctx.strokeStyle = isDark ? darkColor : lightColor;
    ctx.lineWidth = thickness;
    ctx.beginPath();
    ctx.moveTo(0, y);

    const amplitude = opts.amplitudeMin + r3 * (opts.amplitudeMax - opts.amplitudeMin);
    const frequency = opts.frequencyMin + r4 * (opts.frequencyMax - opts.frequencyMin);
    const phase = r5 * Math.PI * 2;
    const amp2 = amplitude * 0.3 * pseudoRandom(seed + i * 37);
    const freq2 = frequency * (2 + pseudoRandom(seed + i * 41));

    for (let x = 0; x <= w; x += 3) {
      const waveY = y
        + Math.sin(x * frequency + phase) * amplitude
        + Math.sin(x * freq2 + phase * 1.7) * amp2;
      ctx.lineTo(x, waveY);
    }
    ctx.stroke();
  }
}

function pseudoRandom(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

function hashColor(color: string): number {
  let hash = 0;
  for (let i = 0; i < color.length; i++) {
    hash = (hash * 31 + color.charCodeAt(i)) | 0;
  }
  return hash;
}
